/**
 * Shared mapping from questionnaire answers to grid coordinates
 * This mapping is used by both the frontend preview and backend analysis engine
 * 
 * Grid Layout (4x4):
 * ┌────┬────┬────┬────┐
 * │ NW │ N  │ N  │ NE │  Row 0
 * ├────┼────┼────┼────┤
 * │ W  │ C  │ C  │ E  │  Row 1
 * ├────┼────┼────┼────┤
 * │ W  │ C  │ C  │ E  │  Row 2
 * ├────┼────┼────┼────┤
 * │ SW │ S  │ S  │ SE │  Row 3
 * └────┴────┴────┴────┘
 *   0    1    2    3
 */

// Direction to grid coordinates mapping
export const directionToGrid = {
  'n': [{ row: 0, col: 1 }, { row: 0, col: 2 }],
  'north': [{ row: 0, col: 1 }, { row: 0, col: 2 }],
  'ne': [{ row: 0, col: 3 }],
  'northeast': [{ row: 0, col: 3 }],
  'e': [{ row: 1, col: 3 }, { row: 2, col: 3 }],
  'east': [{ row: 1, col: 3 }, { row: 2, col: 3 }],
  'se': [{ row: 3, col: 3 }],
  'southeast': [{ row: 3, col: 3 }],
  's': [{ row: 3, col: 1 }, { row: 3, col: 2 }],
  'south': [{ row: 3, col: 1 }, { row: 3, col: 2 }],
  'sw': [{ row: 3, col: 0 }],
  'southwest': [{ row: 3, col: 0 }],
  'w': [{ row: 1, col: 0 }, { row: 2, col: 0 }],
  'west': [{ row: 1, col: 0 }, { row: 2, col: 0 }],
  'nw': [{ row: 0, col: 0 }],
  'northwest': [{ row: 0, col: 0 }],
  'center': [{ row: 1, col: 1 }, { row: 1, col: 2 }, { row: 2, col: 1 }, { row: 2, col: 2 }],
};

// Single cell mapping for primary position
export const directionToPrimaryCell = {
  'n': { row: 0, col: 1 },
  'north': { row: 0, col: 1 },
  'ne': { row: 0, col: 3 },
  'northeast': { row: 0, col: 3 },
  'e': { row: 1, col: 3 },
  'east': { row: 1, col: 3 },
  'se': { row: 3, col: 3 },
  'southeast': { row: 3, col: 3 },
  's': { row: 3, col: 2 },
  'south': { row: 3, col: 2 },
  'sw': { row: 3, col: 0 },
  'southwest': { row: 3, col: 0 },
  'w': { row: 2, col: 0 },
  'west': { row: 2, col: 0 },
  'nw': { row: 0, col: 0 },
  'northwest': { row: 0, col: 0 },
  'center': { row: 1, col: 1 },
};

// Room types with their icons (using lucide icon names)
export const roomIcons = {
  entrance: 'DoorOpen',
  kitchen: 'ChefHat',
  bedroom: 'Bed',
  bathroom: 'Bath',
  puja: 'Flame',
  staircase: 'ArrowUpRight',
  undergroundTank: 'Droplets',
  overheadTank: 'CloudRain',
};

// Room colors for visual distinction
export const roomColors = {
  entrance: '#F59E0B', // Amber
  kitchen: '#EF4444', // Red (fire element)
  bedroom: '#8B5CF6', // Purple
  bathroom: '#3B82F6', // Blue (water)
  puja: '#F97316', // Orange (spiritual)
  staircase: '#6B7280', // Gray
  undergroundTank: '#06B6D4', // Cyan
  overheadTank: '#0EA5E9', // Sky blue
};

// Unknown/unsure marker
export const UNKNOWN_MARKER = 'unsure';

/**
 * Convert questionnaire answers to grid items for preview
 * @param {Object} answers - The questionnaire answers object
 * @returns {Array} Array of grid items with position and type
 */
export const answersToGridItems = (answers) => {
  const items = [];

  // Entrance position
  if (answers.entrancePosition && answers.entrancePosition !== UNKNOWN_MARKER) {
    const cell = directionToPrimaryCell[answers.entrancePosition.toLowerCase()];
    if (cell) {
      items.push({ ...cell, type: 'entrance', label: 'Entry' });
    }
  }

  // Kitchen location
  if (answers.kitchenLocation && answers.kitchenLocation !== UNKNOWN_MARKER) {
    const cell = directionToPrimaryCell[answers.kitchenLocation.toLowerCase()];
    if (cell) {
      items.push({ ...cell, type: 'kitchen', label: 'Kitchen' });
    }
  }

  // Bedroom location
  if (answers.bedroomLocation && answers.bedroomLocation !== UNKNOWN_MARKER) {
    const cell = directionToPrimaryCell[answers.bedroomLocation.toLowerCase()];
    if (cell) {
      items.push({ ...cell, type: 'bedroom', label: 'Bedroom' });
    }
  }

  // Bathroom locations (can be multiple)
  if (answers.bathroomLocations && Array.isArray(answers.bathroomLocations)) {
    answers.bathroomLocations.forEach((loc, index) => {
      if (loc && loc !== UNKNOWN_MARKER) {
        const cell = directionToPrimaryCell[loc.toLowerCase()];
        if (cell) {
          // Offset slightly for multiple bathrooms to avoid overlap
          items.push({ 
            row: cell.row, 
            col: cell.col, 
            type: 'bathroom', 
            label: index === 0 ? 'Bath' : `Bath ${index + 1}` 
          });
        }
      }
    });
  }

  // Puja area
  if (answers.pujaArea && answers.pujaArea !== 'no-area' && answers.pujaLocation && answers.pujaLocation !== UNKNOWN_MARKER) {
    const cell = directionToPrimaryCell[answers.pujaLocation.toLowerCase()];
    if (cell) {
      items.push({ ...cell, type: 'puja', label: 'Puja' });
    }
  }

  // Water elements and staircase
  if (answers.waterElements) {
    const { undergroundTank, overheadTank, staircase, tankLocations } = answers.waterElements;

    if (undergroundTank && tankLocations?.undergroundTank && tankLocations.undergroundTank !== UNKNOWN_MARKER) {
      const cell = directionToPrimaryCell[tankLocations.undergroundTank.toLowerCase()];
      if (cell) {
        items.push({ ...cell, type: 'undergroundTank', label: 'Tank' });
      }
    }

    if (overheadTank && tankLocations?.overheadTank && tankLocations.overheadTank !== UNKNOWN_MARKER) {
      const cell = directionToPrimaryCell[tankLocations.overheadTank.toLowerCase()];
      if (cell) {
        items.push({ ...cell, type: 'overheadTank', label: 'O.Tank' });
      }
    }

    if (staircase && tankLocations?.staircase && tankLocations.staircase !== UNKNOWN_MARKER) {
      const cell = directionToPrimaryCell[tankLocations.staircase.toLowerCase()];
      if (cell) {
        items.push({ ...cell, type: 'staircase', label: 'Stairs' });
      }
    }
  }

  return items;
};

/**
 * Get the facing direction arrow rotation
 * @param {string} direction - The facing direction
 * @returns {number} Rotation in degrees
 */
export const getDirectionRotation = (direction) => {
  const rotations = {
    'n': 0, 'north': 0,
    'ne': 45, 'northeast': 45,
    'e': 90, 'east': 90,
    'se': 135, 'southeast': 135,
    's': 180, 'south': 180,
    'sw': 225, 'southwest': 225,
    'w': 270, 'west': 270,
    'nw': 315, 'northwest': 315,
  };
  return rotations[direction?.toLowerCase()] ?? 0;
};
